import { ParseTimeDurationType } from "./types";

const KEY_VALUE_SEPARATOR = "=";
const PAIR_SEPARATOR = "/";

function decodeMixedURL(urlString: string) {
  let decodedURL: string;
  try {
    decodedURL = decodeURIComponent(urlString);
  } catch {
    const parts = urlString.split("/");
    const decodedParts = parts.map(part => {
      return part.replace(/%[0-9A-Fa-f]{2}/g, match => {
        return String.fromCharCode(parseInt(match.substring(1), 16));
      });
    });
    decodedURL = decodedParts.join("/");
  }
  return decodedURL;
}

/**
 * Given a dimension string, transform it into a hash
 * This function will cache the results by default
 *
 * dimesionString format => `hostname=id-123/key=value`
 * { hostname: "id-123", key: "value" }
 */
const parseDimensions = (dimensionString?: string) => {
  if (typeof dimensionString !== "string") {
    return;
  }

  const dimensions = dimensionString.split(PAIR_SEPARATOR);
  const dimensionsObject: Record<string, string> = {};

  for (let i = 0; i < dimensions.length; i++) {
    const dimension = dimensions[i];
    if (dimension) {
      const [key, value] = dimension.split(KEY_VALUE_SEPARATOR);
      if (key) {
        dimensionsObject[key] = decodeMixedURL(value);
      }
    }
  }
  return dimensionsObject;
};

/**
 * parse a string representation of time duration
 * @param timeUnit string of time duration
 * @param fallbackMinute fallback number in minute if timeunit is invalid
 * @returns
 */
export const parseTimeDuration = (
  timeUnit: string = "",
  fallbackMinute: number = 15
): ParseTimeDurationType => {
  let seconds = 0;
  const durationRegex = /(-?\d+)([smhdwMy]{1})/g;
  let durationMatch = durationRegex.exec(timeUnit);
  const unitValues: {
    unit: "s" | "m" | "h" | "d" | "w" | "M" | "y";
    value: number;
  }[] = [];

  const unitToSeconds: Record<string, number> = {
    s: 1,
    m: 60,
    h: 3600,
    d: 86400,
    w: 604800,
    M: 2592000,
    y: 31536000
  };

  while (durationMatch) {
    const unit = durationMatch[2] as "s" | "m" | "h" | "d" | "w" | "M" | "y";
    const value = parseInt(durationMatch[1], 10);

    unitValues.push({ unit, value });
    seconds += value * unitToSeconds[unit];
    durationMatch = durationRegex.exec(timeUnit);
  }

  const durationOrder = {
    s: { priority: 1, label: "second" },
    m: { priority: 2, label: "minute" },
    h: { priority: 3, label: "hour" },
    d: { priority: 4, label: "day" },
    w: { priority: 5, label: "week" },
    M: { priority: 6, label: "month" },
    y: { priority: 7, label: "year" }
  } as const;

  const convertToWord = (data: typeof unitValues) => {
    const sorted = data.sort((a, b) => {
      const diff =
        b.value * unitToSeconds[b.unit] - a.value * unitToSeconds[a.unit];
      return diff;
    });
    const pluralise = sorted.map(({ unit, value }) => {
      // Pluralise if unit is greater than 1
      return value > 1
        ? { label: `${durationOrder[unit].label}s`, value }
        : { label: durationOrder[unit].label, value };
    });

    return pluralise
      .reduce((word, next) => word.concat(`${next.value} ${next.label} `), "")
      .trim();
  };

  if (!seconds) {
    const fallbackSeconds = fallbackMinute * 60;
    return {
      seconds: fallbackSeconds,
      milliseconds: fallbackSeconds * 1000,
      durationText: convertToWord([{ unit: "m", value: fallbackMinute }]),
      type: "minute" as const,
      range: fallbackMinute,
      typePlural: "minutes"
    };
  }

  /* If more than one time duration unit, we can't tell the type so lets treat as seconds
   * Single Unit E.g 1h, 2d, 5d, 1m, 30d,
   * Not single unit E.g 1M2d, 2y5M2d
   */

  const isOneUnit = unitValues.length === 1;

  const type = isOneUnit ? durationOrder[unitValues[0].unit].label : "second";
  const range = isOneUnit ? unitValues[0].value : seconds;

  const pluraliseType = ({ range, type }: { range: number; type: string }) =>
    range > 1 ? `${type}s` : type;

  return {
    milliseconds: seconds * 1000,
    seconds,
    durationText: convertToWord(unitValues),
    type,
    range,
    typePlural: pluraliseType({ type, range })
  };
};

const pluralize = (value: number, unit: string): string => {
  let result = `${value} ${unit}`;
  if (value > 1) {
    result += "s";
  }
  return result;
};

const aYearInMinutes = 365 * 24 * 60;
const aMonthInMinutes = 30 * 24 * 60;
const aWeekInMinutes = 7 * 24 * 60;
const aDayInMinutes = 1 * 24 * 60;
const aHourInMinutes = 1 * 60;

export const parseTimeDurationToString = (
  durationInMinutes: number
): string => {
  const years = Math.floor(durationInMinutes / aYearInMinutes);
  durationInMinutes -= years * aYearInMinutes;
  const months = Math.floor(durationInMinutes / aMonthInMinutes);
  durationInMinutes -= months * aMonthInMinutes;
  const weeks = Math.floor(durationInMinutes / aWeekInMinutes);
  durationInMinutes -= weeks * aWeekInMinutes;
  const days = Math.floor(durationInMinutes / aDayInMinutes);
  durationInMinutes -= days * aDayInMinutes;
  const hours = Math.floor(durationInMinutes / aHourInMinutes);
  durationInMinutes -= hours * aHourInMinutes;

  const minutes = durationInMinutes % 60;

  const output: string[] = [];

  if (years > 0) {
    output.push(`${pluralize(years, "year")}`);
  }

  if (months > 0) {
    output.push(`${pluralize(months, "month")}`);
  }

  if (weeks > 0) {
    output.push(`${pluralize(weeks, "week")}`);
  }

  if (days > 0) {
    output.push(`${pluralize(days, "day")}`);
  }

  if (hours > 0) {
    output.push(`${pluralize(hours, "hour")}`);
  }

  if (minutes > 0) {
    output.push(`${pluralize(minutes, "minute")}`);
  }

  return output.join(" ");
};

export const parseNumberToParseDuration = (
  durationMinutes: number
): ParseTimeDurationType => {
  return {
    milliseconds: durationMinutes * 60 * 1000,
    seconds: durationMinutes * 60,
    durationText: parseTimeDurationToString(durationMinutes),
    type: "minute" as const,
    range: durationMinutes,
    typePlural: pluralize(durationMinutes, "minute")
  };
};

export default parseDimensions;
